import { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useCurrentDeal } from "@/context/CurrentDealContext";
import api from "@/lib/api";
import { Avatar } from "@/components/common";
import CommandPalette from "@/components/CommandPalette";
import NotificationBell from "@/components/NotificationBell";
import RegistrarAtividade from "@/components/RegistrarAtividade";
import { roleLabel } from "@/lib/format";
import { APP_VERSION } from "@/lib/version";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { MpiresLogo, AlliaBadge } from "@/components/Brand";
import {
  LayoutDashboard, Home, KanbanSquare, Users, CalendarClock, AlertTriangle, TrendingUp,
  FileBarChart, Trophy, UsersRound, MapPin, Camera, Server, Search, Settings, UploadCloud,
  LogOut, Menu, Building2, Contact, ClipboardList, Wallet, ChevronRight, ShieldAlert, Link2, DollarSign, Plus, Calculator, Database,
} from "@/components/icons";

const ICON = {
  LayoutDashboard, Home, KanbanSquare, Users, CalendarClock, AlertTriangle, TrendingUp,
  FileBarChart, Trophy, UsersRound, MapPin, Camera, Server, Search, Contact, ClipboardList, Wallet, UploadCloud, ShieldAlert, Link2, DollarSign, Plus, Calculator, Database,
};

function navFor(user, extra = {}) {
  const role = user?.role;
  const managerish = ["admin", "director", "manager", "diretor_geral"].includes(role);
  const canCaptar = ["admin", "director", "manager"].includes(role);
  const canFaturamento = ["admin", "diretor_geral"].includes(role) || extra?.faturamentoLiberado === true;
  const items = [];
  items.push(
    managerish
      ? { to: "/", label: "Painel", icon: "Home", end: true }
      : { to: "/", label: "Início", icon: "Home", end: true },
    { to: "/pipeline", label: "Funil", icon: "KanbanSquare" },
    { to: "/clientes", label: "Prospects e clientes", icon: "Users" },
  );
  items.push(
    { to: "/atividades", label: "Agenda", icon: "CalendarClock" },
    { to: "/atencao", label: "Requer atenção", icon: "AlertTriangle" },
    { to: "/previsao", label: "Previsão", icon: "TrendingUp" },
  );
  if (canFaturamento) items.push({ to: "/faturamento", label: "Faturamento", icon: "DollarSign" });
  items.push({ to: "/relatorios", label: "Relatórios", icon: "FileBarChart" });
  items.push({ to: "/potenciais", label: "Indicações do gestor", icon: "Contact" });
  items.push({ to: "/captar", label: "Prospectar", icon: "MapPin" });
  // M-1: item "Custo por uso" só para gestores (gerente, diretor, DG). Abre
  // /custo-por-uso em nova aba com os dados do usuário logado e SEM cliente —
  // mesma pattern do botão "Custo/uso" da ficha do cliente, só que sem
  // clienteId/segmento/porte. Marcado com `external: true` para que
  // `NavItem` renderize como <button> (nova aba) ao invés de <NavLink>.
  if (["manager", "director", "diretor_geral"].includes(role)) {
    items.push({ external: true, key: "custo-por-uso", label: "Custo por uso", icon: "Calculator" });
  }
  items.push({ to: "/treinamentos", label: "Treinamentos", icon: "GraduationCap" });
  if (role === "admin") items.push({ to: "/ti", label: "Usuários", icon: "Server" });
  if (["admin", "diretor_geral"].includes(role)) items.push({ to: "/ajustes/sankhya", label: "Integração Sankhya", icon: "Link2" });
  if (role === "admin") items.push({ to: "/importar-clientes-ativos", label: "Clientes ativos MPires", icon: "UploadCloud" });
  if (role === "admin") items.push({ to: "/importar-carteira-sankhya", label: "Carteira ativa do Sankhya", icon: "Database" });
  return items;
}

/** Grupo "Gestão" no rodapé do menu. Auditoria de cadastro é restrita a Admin
 * (regra confirmada pelo usuário na auditoria completa v4). "Sem interação" e
 * "Carteira da equipe" continuam visíveis para toda gestão até a unificação
 * dos dois na aba de Equipe (aguardando validação de campos). */
function gestaoNavFor(user) {
  const role = user?.role;
  const managerish = ["admin", "director", "manager", "diretor_geral"].includes(role);
  if (!managerish) return [];
  const items = [
    { to: "/equipe", label: "Equipe · Cotas", icon: "UsersRound" },
    // LOTE UX-2B item 1: "Sem interação" vira filtro dentro de "Carteira
    // da equipe". O link antigo (?visao=sem-interacao) segue funcionando.
    { to: "/carteira", label: "Carteira da equipe", icon: "Wallet" },
  ];
  if (role === "admin") {
    items.push({ to: "/admin/auditoria-cadastro", label: "Auditoria de cadastro", icon: "ShieldAlert" });
  }
  return items;
}

function bottomNavFor(user) {
  const role = user?.role;
  // Item 3.14 — barra inferior por papel.
  const managerish = ["director", "diretor_geral"].includes(role);
  if (managerish) {
    return [
      { to: "/", label: "Início", icon: "Home", end: true },
      { to: "/pipeline", label: "Funil", icon: "KanbanSquare" },
      { to: "/relatorios", label: "Relatórios", icon: "FileBarChart", highlight: true },
      { to: "/equipe", label: "Equipe", icon: "UsersRound" },
    ];
  }
  // Lote 2 UX-19/20/CRM-032 — Vendedor, Gerente e Administrador:
  // 5 itens, "Mais" removido (o menu ≡ do cabeçalho tem toda a navegação).
  // Centro é botão não-rota ("plus") que abre o modal Registrar atividade.
  return [
    { to: "/", label: "Início", icon: "Home", end: true },
    { to: "/atividades", label: "Agenda", icon: "CalendarClock" },
    { to: "__plus__", label: "Registrar atividade", icon: "Plus", action: "registrar", highlight: true },
    { to: "/pipeline", label: "Funil", icon: "KanbanSquare" },
    { to: "/clientes", label: "Clientes", icon: "Users" },
  ];
}

// Lote 2 item 4 — overrides de "item ativo" por prefixo de rota.
// NavLink por padrão só casa prefixos literais. Para /cliente/:id (singular)
// não bate em /clientes (plural); idem /deal/:id vs /pipeline; /captar/mapa
// vs /captar. Mapeia explicitamente.
function navItemActive(pathname, to, end) {
  if (to === "/clientes" && (pathname === "/cliente" || pathname.startsWith("/cliente/"))) return true;
  if (to === "/pipeline" && (pathname === "/deal" || pathname.startsWith("/deal/"))) return true;
  if (to === "/captar" && pathname.startsWith("/captar")) return true;
  if (end) return pathname === to;
  return pathname === to || pathname.startsWith(to + "/");
}

// Item 3.3 — o painel "Mais" mostra APENAS o que não está na barra inferior.
function morePanelFor(user, bottomTos, extra = {}) {
  const inBottom = new Set(bottomTos);
  // M-1: item externo (sem `to`) nunca está na barra inferior; preserva no painel.
  const items = navFor(user, extra).filter((it) => it.external || !inBottom.has(it.to));
  const gestao = gestaoNavFor(user).filter((it) => !inBottom.has(it.to));
  return { primary: items, gestao };
}

function NavItem({ item, onClick, badge }) {
  const Icon = ICON[item.icon] || LayoutDashboard;
  const location = useLocation();
  // M-1: item externo (nova aba) renderiza como <button> com o MESMO estilo
  // de um NavLink inativo. Mantém o visual uniforme no menu.
  if (item.external) {
    return (
      <button
        type="button"
        onClick={(e) => { item.onClick?.(e); onClick?.(e); }}
        data-testid={`nav-${item.key || "ext"}`}
        className={cn(
          "w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors text-left",
          "text-slate-600 hover:bg-slate-100 hover:text-navy"
        )}
      >
        <Icon className="w-[18px] h-[18px] shrink-0" />
        <span className="truncate notranslate flex-1" translate="no">{item.label}</span>
      </button>
    );
  }
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onClick}
      data-testid={`nav-${item.to === "/" ? "inicio" : item.to.slice(1)}`}
      className={() => {
        const isActive = navItemActive(location.pathname, item.to, item.end);
        return cn(
          "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
          isActive ? "bg-brand text-white shadow-sm" : "text-slate-600 hover:bg-slate-100 hover:text-navy"
        );
      }}
    >
      <Icon className="w-[18px] h-[18px] shrink-0" />
      <span className="truncate notranslate flex-1" translate="no">{item.label}</span>
      {badge > 0 ? (
        <span
          data-testid={`nav-badge-${item.to.slice(1) || "inicio"}`}
          className="min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[10px] font-bold flex items-center justify-center"
        >
          {badge > 99 ? "99+" : badge}
        </span>
      ) : null}
    </NavLink>
  );
}

function SidebarContent({ user, onNavigate, logout, onSearch, showBell, faturamentoLiberado }) {
  const location = useLocation();
  const nav = navFor(user, { faturamentoLiberado });
  const gestao = gestaoNavFor(user);
  const [gestaoOpen, setGestaoOpen] = useState(false);
  // Badge de "Novas captações" no item Clientes — Auditoria v4, Parte 4.2.
  // Só faz sentido pra papéis que veem captações (comercial). Falha silenciosa.
  const [novasCaptacoes, setNovasCaptacoes] = useState(0);
  useEffect(() => {
    let alive = true;
    if (!user || !["seller", "manager", "director", "admin", "diretor_geral"].includes(user.role)) return;
    api.get("/captacoes").then((r) => { if (alive) setNovasCaptacoes(r.data?.novas || 0); }).catch(() => {});
    return () => { alive = false; };
  }, [user]);
  const badgeFor = (to) => (to === "/clientes" ? novasCaptacoes : 0);
  // M-1: builder do querystring para abrir /custo-por-uso em nova aba a partir
  // do item do menu. Mesma receita do openCustoPorUso de ClienteDetalhe.js
  // (ficha do cliente), só que SEM dados de cliente (clienteId/cliente/
  // segmento/porte vazios) — campo "Cliente" na calculadora fica em branco
  // para digitar.
  const openCustoPorUso = () => {
    const enc = encodeURIComponent;
    const u = user || {};
    const qs = [
      ["clienteId", ""], ["cliente", ""], ["segmento", ""], ["porte", ""],
      ["vendedorId", u.id || ""], ["vendedor", u.name || ""],
      ["cargo", u.cargo || u.role || ""], ["fone", u.phone || ""], ["email", u.email || ""],
    ].map(([k, v]) => `${k}=${enc(v)}`).join("&");
    window.open(`/custo-por-uso?${qs}`, "_blank", "noopener");
  };
  const navEnriched = nav.map((it) => (it.external && it.key === "custo-por-uso" ? { ...it, onClick: openCustoPorUso } : it));
  return (
    <div className="flex flex-col h-full w-full min-w-0">
      <div className="px-5 py-5 border-b border-black/5 flex items-start justify-between">
        <div>
          <MpiresLogo height={128} />
          <div className="mt-2"><AlliaBadge /></div>
        </div>
        {showBell && <NotificationBell />}
      </div>
      {onSearch && (
        <div className="px-3 pt-3">
          <button onClick={onSearch} data-testid="sidebar-search-btn"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg border border-input bg-slate-50 text-sm text-slate-500 hover:bg-slate-100 hover:border-brand/30 transition-colors">
            <Search className="w-4 h-4" />
            <span className="flex-1 text-left">Buscar...</span>
            <kbd className="text-[10px] font-semibold text-slate-400 bg-white border border-black/5 rounded px-1.5 py-0.5">⌘K</kbd>
          </button>
        </div>
      )}
      <nav
        className="flex-1 min-h-0 px-3 py-4 space-y-1 sidebar-scroll notranslate overflow-y-auto"
        translate="no"
      >
        {navEnriched.map((item) => (
          <NavItem key={item.to || item.key} item={item} onClick={onNavigate} badge={item.to ? badgeFor(item.to) : 0} />
        ))}
        {gestao.length > 0 && (
          <div className="pt-3 mt-3 border-t border-black/5" data-testid="nav-grupo-gestao">
            <button
              onClick={() => setGestaoOpen((v) => !v)}
              data-testid="nav-grupo-gestao-toggle"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider text-slate-500 hover:bg-slate-100"
            >
              <ChevronRight className={cn("w-3.5 h-3.5 transition-transform", gestaoOpen && "rotate-90")} />
              Gestão
              <span className="ml-auto text-[10px] font-normal text-slate-400">{gestao.length}</span>
            </button>
            {gestaoOpen && (
              <div className="mt-1 space-y-1">
                {gestao.map((item) => (
                  <NavItem key={item.to} item={item} onClick={onNavigate} />
                ))}
              </div>
            )}
          </div>
        )}
      </nav>
      <div className="border-t border-black/5 p-3">
        <NavLink
          to="/perfil"
          onClick={onNavigate}
          data-testid="nav-perfil"
          className="flex items-center gap-3 px-2 py-2 rounded-lg hover:bg-slate-100 transition-colors"
        >
          <Avatar name={user?.name} src={user?.avatar} size={36} />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-navy truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">{roleLabel(user?.role)}</div>
          </div>
        </NavLink>
        <div className="flex items-center gap-1 mt-2">
          <NavLink
            to="/configuracoes"
            onClick={onNavigate}
            data-testid="nav-configuracoes"
            className="flex-1 flex items-center gap-2 px-2 py-2 rounded-lg text-xs text-slate-600 hover:bg-slate-100 transition-colors"
          >
            <Settings className="w-4 h-4" /> Ajustes
          </NavLink>
          <button
            onClick={logout}
            data-testid="logout-btn"
            className="flex items-center gap-2 px-2 py-2 rounded-lg text-xs text-bad hover:bg-bad-soft transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sair
          </button>
        </div>
        <div className="text-center text-[10px] text-slate-400 mt-2">{APP_VERSION}</div>
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);
  // Lote 2 UX-19/20: botão central "+" da barra inferior abre o modal
  // Registrar atividade sem pré-seleção (igual ao botão do Início).
  const [registrarOpen, setRegistrarOpen] = useState(false);
  const [carteira, setCarteira] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const crmUser = true;
  useEffect(() => {
    if (!registrarOpen || carteira.length) return;
    api.get("/clientes").then((r) => setCarteira(r.data || [])).catch(() => {});
  }, [registrarOpen, carteira.length]);
  // Estado ÚNICO de liberação — compartilhado entre a barra do computador e o
  // menu ☰ do celular. Antes ficava só em SidebarContent, o que fazia o menu
  // do celular nunca receber `faturamentoLiberado`. (Diagnóstico do suporte
  // Emergent, 24/09.)
  const [faturamentoLiberado, setFaturamentoLiberado] = useState(false);
  useEffect(() => {
    if (["admin", "diretor_geral"].includes(user?.role)) { setFaturamentoLiberado(true); return; }
    if (!user) { setFaturamentoLiberado(false); return; }
    let cancel = false;
    const fetchIt = async () => {
      try {
        const { data } = await api.get("/sankhya/liberado-para-mim");
        if (!cancel) {
          setFaturamentoLiberado(!!data?.liberado);
          if (!data?.liberado) console.warn("[Faturamento] menu oculto —", data?.motivo || "sem motivo");
        }
      } catch (e) {
        if (!cancel) { setFaturamentoLiberado(false); console.warn("[Faturamento] erro ao consultar liberacao", e); }
      }
    };
    fetchIt();
    const onVis = () => { if (document.visibilityState === "visible") fetchIt(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancel = true; document.removeEventListener("visibilitychange", onVis); };
  }, [user?.role, user, location.pathname]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setCmdOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [crmUser]);

  // Item 3.14 — barra inferior varia por papel. Item central destacado.
  const bottom = bottomNavFor(user);
  const bottomTos = bottom.map((i) => i.to);
  const morePanel = crmUser ? morePanelFor(user, bottomTos, { faturamentoLiberado }) : { primary: [], gestao: [] };
  // M-1: mesmo builder usado no SidebarContent — abre a calculadora em nova
  // aba com os dados do usuário logado e SEM cliente. Replicado aqui porque
  // o Sheet mobile renderiza `morePanel.primary` fora do SidebarContent.
  const openCustoPorUsoMobile = () => {
    const enc = encodeURIComponent;
    const u = user || {};
    const qs = [
      ["clienteId", ""], ["cliente", ""], ["segmento", ""], ["porte", ""],
      ["vendedorId", u.id || ""], ["vendedor", u.name || ""],
      ["cargo", u.cargo || u.role || ""], ["fone", u.phone || ""], ["email", u.email || ""],
    ].map(([k, v]) => `${k}=${enc(v)}`).join("&");
    window.open(`/custo-por-uso?${qs}`, "_blank", "noopener");
  };
  const morePanelEnriched = {
    ...morePanel,
    primary: morePanel.primary.map((it) => (it.external && it.key === "custo-por-uso" ? { ...it, onClick: openCustoPorUsoMobile } : it)),
  };

  return (
    <div className="min-h-screen bg-background">
      {crmUser && <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} />}
      {/* Item 3.5 — sidebar a partir de 768px (md). */}
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 bg-card border-r border-black/5 z-30">
        <SidebarContent user={user} logout={logout} onNavigate={() => {}} onSearch={crmUser ? () => setCmdOpen(true) : null} showBell={crmUser} faturamentoLiberado={faturamentoLiberado} />
      </aside>

      {/* Header mobile — Parte 3 do pedido REGISTRAR_CONTATO_E_VISITA:
          barra branca ~72 px (era ~52 px), logo ~48 px inteiro dentro da barra.
          Logo do menu ☰ vai a ~56 px também dentro da faixa branca. */}
      <header
        className="md:hidden sticky top-0 z-20 flex items-center justify-between px-3 bg-white border-b border-black/5"
        style={{ paddingTop: "env(safe-area-inset-top)", height: "calc(4.5rem + env(safe-area-inset-top))" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <MpiresLogo height={60} trim />
        </div>
        {/* Item 3.6 — botões do header ≥44×44 e aria-label. */}
        <div className="flex items-center gap-1">
          {crmUser && <NotificationBell />}
          {crmUser && (
            <button data-testid="mobile-search-btn" onClick={() => setCmdOpen(true)} aria-label="Buscar"
              className="h-11 w-11 flex items-center justify-center rounded-lg hover:bg-slate-100">
              <Search className="w-5 h-5 text-navy" />
            </button>
          )}
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-btn" aria-label="Menu de navegação"
                className="h-11 w-11 flex items-center justify-center rounded-lg hover:bg-slate-100">
                <Menu className="w-5 h-5 text-navy" />
              </button>
            </SheetTrigger>
            {/* UI-1 (item 6) — menu sempre abre no topo (sem rolagem resgatada),
             *  respeitando a área segura do iPhone, com logo visível no topo,
             *  lista rolável só no meio e Ajustes/Sair/versão presos embaixo. */}
            <SheetContent
              side="left"
              className="p-0 w-80 flex flex-col"
              style={{ height: "100dvh" }}
            >
              <SheetTitle className="sr-only">Mais</SheetTitle>
              <div
                className="px-5 py-5 border-b border-black/5 shrink-0 bg-white"
                style={{ paddingTop: "calc(1.25rem + env(safe-area-inset-top))" }}
                data-testid="mobile-menu-header"
              >
                <MpiresLogo height={56} />
                <div className="mt-2"><AlliaBadge /></div>
              </div>
              <nav
                ref={(el) => { if (open && el) el.scrollTop = 0; }}
                className="px-3 py-4 space-y-1 flex-1 overflow-y-auto"
                data-testid="mobile-menu-nav"
              >
                {morePanelEnriched.primary.map((it) => (
                  <NavItem key={it.to || it.key} item={it} onClick={() => setOpen(false)} />
                ))}
                {morePanelEnriched.gestao.length > 0 && (
                  <>
                    <div className="mt-3 px-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Gestão</div>
                    {morePanelEnriched.gestao.map((it) => (
                      <NavItem key={it.to} item={it} onClick={() => setOpen(false)} />
                    ))}
                  </>
                )}
              </nav>
              <div
                className="border-t border-black/5 p-4 space-y-2 shrink-0 bg-white"
                style={{ paddingBottom: "calc(1rem + env(safe-area-inset-bottom))" }}
              >
                <button onClick={() => { setOpen(false); navigate("/configuracoes"); }} data-testid="more-ajustes"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-slate-100">
                  <Settings className="w-[18px] h-[18px]" /> Ajustes
                </button>
                <button onClick={() => { setOpen(false); logout(); navigate("/login"); }} data-testid="more-logout"
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-bad hover:bg-bad-soft">
                  <LogOut className="w-[18px] h-[18px]" /> Sair
                </button>
                <div className="text-center text-[10px] text-slate-400 pt-1" data-testid="mobile-menu-version">{APP_VERSION}</div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>

      {/* Conteúdo */}
      <main
        className="md:pl-64 pb-20 md:pb-0"
        style={{ paddingBottom: "calc(5rem + env(safe-area-inset-bottom))" }}
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 md:py-8 animate-in-up" key={location.pathname}>
          {children}
        </div>
      </main>

      {/* Bottom nav — apenas < 768px (item 3.5)
          Lote 2 UX-19/20: 5 itens para Vendedor/Gerente/Admin, "Mais"
          removido (toda a navegação está no menu ≡ do cabeçalho). O item
          central é botão (não rota) que abre o modal Registrar atividade.
          Diretor e Diretor Geral mantidos como estavam. */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-20 bg-white border-t border-black/5 flex items-stretch h-16"
        style={{ paddingBottom: "env(safe-area-inset-bottom)", height: "calc(4rem + env(safe-area-inset-bottom))" }}
        aria-label="Navegação principal"
      >
        {bottom.map((item) => {
          const Icon = ICON[item.icon];
          // Pseudo-item "+" central: botão, sem rota, abre Registrar atividade.
          if (item.action === "registrar") {
            return (
              <button
                key="__plus__"
                type="button"
                onClick={() => setRegistrarOpen(true)}
                data-testid="tab-registrar-atividade"
                aria-label={item.label}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold"
              >
                <div className="w-11 h-11 -mt-4 rounded-full bg-brand shadow-lg flex items-center justify-center ring-4 ring-white">
                  <Icon className="w-5 h-5 text-white" />
                </div>
              </button>
            );
          }
          if (item.highlight) {
            return (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.end}
                data-testid={`tab-${item.to === "/" ? "inicio" : item.to.slice(1)}`}
                className="flex-1 flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold text-white"
              >
                <div className="w-11 h-11 -mt-4 rounded-full bg-brand shadow-lg flex items-center justify-center ring-4 ring-white">
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <span className="text-brand mt-0.5 whitespace-nowrap">{item.label}</span>
              </NavLink>
            );
          }
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={`tab-${item.to === "/" ? "inicio" : item.to.slice(1)}`}
              className={() => {
                const isActive = navItemActive(location.pathname, item.to, item.end);
                return cn(
                  "flex-1 flex flex-col items-center justify-center gap-1 text-[11px] font-semibold transition-colors min-h-[44px]",
                  isActive ? "text-brand" : "text-navy"
                );
              }}
            >
              {({ isActive }) => (
                <>
                  <span className={cn(
                    "flex items-center justify-center rounded-full transition-colors",
                    isActive ? "bg-brand-soft w-9 h-9" : "w-6 h-6"
                  )}>
                    <Icon className={cn(isActive ? "w-5 h-5" : "w-6 h-6")} strokeWidth={2.25} />
                  </span>
                  <span className="whitespace-nowrap">{item.label}</span>
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      {/* Modal Registrar atividade — acionado pelo "+" central da barra inferior.
          Carteira carregada sob demanda (lazy), igual ao Início.
          LOTE UX-1 A1 — se o usuário está DENTRO de uma oportunidade (contexto
          `CurrentDealContext`), a janela já abre pré-preenchida com cliente +
          oportunidade da própria tela. */}
      <RegistrarAtividadeViaBarra
        open={registrarOpen}
        onOpenChange={setRegistrarOpen}
        carteira={carteira}
      />
    </div>
  );
}

function RegistrarAtividadeViaBarra(props) {
  const { deal } = useCurrentDeal();
  return (
    <RegistrarAtividade
      {...props}
      cliente={deal?.cliente_id ? { id: deal.cliente_id, nome: deal.cliente_nome } : null}
      deal={deal || null}
    />
  );
}
